import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Button, IconButton, Paper, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import {appConfig} from '@/config/appConfig';
import {
    getCapturedInstallPrompt,
    subscribeInstallPrompt,
    clearCapturedInstallPrompt,
    type BeforeInstallPromptEvent,
} from './installPromptCapture';
import './PwaInstallBanner.css';

type Platform = 'ios' | 'android' | null;

const DISMISSED_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const SESSION_KEY = 'pwa-install-shown';
const IOS_DELAY_MS = 2000;

const isShownThisSession = (): boolean => {
    try {
        return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
        return false;
    }
};

const markShownThisSession = (): void => {
    try {
        sessionStorage.setItem(SESSION_KEY, '1');
    } catch { /* storage unavailable */ }
};

const detectMobilePlatform = (): Platform => {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return null;
    const ua = navigator.userAgent;
    const isIOS =
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|UCBrowser|DuckDuckGo|Instagram|FBAN|FBAV/.test(ua);
    const isAndroid = /Android/.test(ua);
    if (isIOS && isSafari) return 'ios';
    if (isAndroid) return 'android';
    return null;
};

const isStandalone = (): boolean => {
    if (typeof window === 'undefined') return false;
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as Navigator & {standalone?: boolean}).standalone === true
    );
};

const isDismissed = (): boolean => {
    try {
        const raw = localStorage.getItem(DISMISSED_KEY);
        if (!raw) return false;
        return Date.now() < parseInt(raw, 10);
    } catch {
        return false;
    }
};

const dismissForAWhile = (): void => {
    try {
        localStorage.setItem(DISMISSED_KEY, String(Date.now() + DISMISS_DURATION_MS));
    } catch { /* storage unavailable */ }
};

const PwaInstallBanner: React.FC = () => {
    const {t} = useTranslation();
    const [platform, setPlatform] = useState<Platform>(null);
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!appConfig.pwa.installBanner) return;
        if (isStandalone() || isDismissed()) return;

        const detected = detectMobilePlatform();
        if (!detected) return;

        if (detected === 'android') {
            const showWith = (e: BeforeInstallPromptEvent) => {
                if (isDismissed() || isShownThisSession()) return;
                setDeferredPrompt(e);
                setPlatform('android');
                markShownThisSession();
                setVisible(true);
            };
            const already = getCapturedInstallPrompt();
            if (already) showWith(already);
            return subscribeInstallPrompt(showWith);
        }

        if (detected === 'ios') {
            const timer = setTimeout(() => {
                if (isDismissed() || isShownThisSession()) return;
                markShownThisSession();
                setPlatform('ios');
                setVisible(true);
            }, IOS_DELAY_MS);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        await deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        dismissForAWhile();
        clearCapturedInstallPrompt();
        setDeferredPrompt(null);
        setVisible(false);
    };

    const handleDismiss = () => {
        dismissForAWhile();
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <Paper
            className="pwa-install-banner"
            elevation={8}
            role="region"
            aria-label={t('pwaInstall.title', {appName: appConfig.app.name})}
        >
            <IconButton
                size="small"
                onClick={handleDismiss}
                className="pwa-install-banner__close"
                aria-label={t('pwaInstall.dismiss')}
            >
                <CloseIcon fontSize="small"/>
            </IconButton>

            <Box className="pwa-install-banner__content">
                <Box
                    component="img"
                    src={appConfig.branding.logo.path}
                    alt={appConfig.branding.logo.alt}
                    className="pwa-install-banner__logo"
                />
                <Box className="pwa-install-banner__body">
                    <Typography variant="subtitle2" className="pwa-install-banner__title">
                        {t('pwaInstall.title', {appName: appConfig.app.name})}
                    </Typography>
                    <Typography variant="body2" className="pwa-install-banner__subtitle">
                        {t('pwaInstall.body')}
                    </Typography>

                    {platform === 'android' && (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleInstall}
                            className="pwa-install-banner__install"
                        >
                            {t('pwaInstall.install')}
                        </Button>
                    )}

                    {platform === 'ios' && (
                        <Box component="ol" className="pwa-install-banner__steps">
                            <li>
                                <span className="pwa-install-banner__step-num">1</span>
                                <span>
                                    {t('pwaInstall.iosStep1Prefix')}{' '}
                                    <IosShareIcon fontSize="inherit" className="pwa-install-banner__inline-icon" aria-hidden="true"/>{' '}
                                    <strong>{t('pwaInstall.share')}</strong>{' '}
                                    {t('pwaInstall.iosStep1Suffix')}
                                </span>
                            </li>
                            <li>
                                <span className="pwa-install-banner__step-num">2</span>
                                <span>
                                    {t('pwaInstall.iosStep2Prefix')}{' '}
                                    <AddBoxOutlinedIcon fontSize="inherit" className="pwa-install-banner__inline-icon" aria-hidden="true"/>{' '}
                                    <strong>{t('pwaInstall.addToHomeScreen')}</strong>
                                </span>
                            </li>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default PwaInstallBanner;
